import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { CheckCircle, SpinnerGap, ArrowLeft, WarningCircle } from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cardio-journey-app/artifacts/tyj7q2ct_logo%20365.png";

const CheckoutSuccess = () => {
  const navigate = useNavigate();
  const { token, updateUser, checkAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollStatus = async () => {
      if (attempts >= 8) {
        setStatus('timeout');
        return;
      }

      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(`${API}/checkout/status/${sessionId}`, {
          withCredentials: true,
          headers,
        });

        if (res.data.payment_status === 'paid') {
          setStatus('paid');
          await checkAuth();
          return;
        } else if (res.data.status === 'expired') {
          setStatus('expired');
          return;
        }

        setAttempts(prev => prev + 1);
        setTimeout(pollStatus, 2500);
      } catch {
        setAttempts(prev => prev + 1);
        setTimeout(pollStatus, 2500);
      }
    };

    pollStatus();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 relative overflow-hidden" data-testid="checkout-success-screen">
      <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ backgroundImage: `url(${LOGO_URL})`, backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundSize: '50%' }} />

      <div className="relative z-10 max-w-md w-full text-center">
        <img src={LOGO_URL} alt="Track365" className="w-32 h-auto mx-auto mb-6" />

        {status === 'checking' && (
          <div data-testid="payment-checking">
            <SpinnerGap weight="bold" className="w-16 h-16 text-[#D4AF37] mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Processing Payment...
            </h1>
            <p className="text-muted-foreground">Please wait while we confirm your payment.</p>
            <div className="mt-4 h-1.5 bg-card rounded-full overflow-hidden max-w-xs mx-auto">
              <div className="h-full bg-[#D4AF37] rounded-full animate-pulse" style={{ width: `${Math.min((attempts + 1) * 15, 90)}%`, transition: 'width 0.5s' }} />
            </div>
          </div>
        )}

        {status === 'paid' && (
          <div data-testid="payment-success">
            <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle weight="fill" className="w-12 h-12 text-green-500" />
            </div>
            <h1 className="text-3xl font-black text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Welcome to <span className="text-[#D4AF37]">Premium!</span>
            </h1>
            <p className="text-muted-foreground mb-6">
              Your subscription is active. Full 365-day tracking unlocked.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-[#D4AF37] text-black font-bold uppercase tracking-widest rounded-full py-4 hover:bg-[#F3E5AB] transition-all text-sm"
              data-testid="go-dashboard-btn"
            >
              Go to Dashboard
            </button>
          </div>
        )}

        {(status === 'expired' || status === 'error' || status === 'timeout') && (
          <div data-testid="payment-error">
            <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
              <WarningCircle weight="fill" className="w-12 h-12 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {status === 'timeout' ? 'Payment Verification Timed Out' : 'Payment Issue'}
            </h1>
            <p className="text-muted-foreground mb-6">
              {status === 'timeout'
                ? 'We couldn\'t confirm your payment yet. Check your email for confirmation.'
                : 'Something went wrong. Please try again.'}
            </p>
            <button
              onClick={() => navigate('/pricing')}
              className="w-full bg-card border border-border text-foreground rounded-full py-4 hover:bg-[#D4AF37]/10 transition-all text-sm font-semibold"
              data-testid="back-pricing-btn"
            >
              <ArrowLeft weight="bold" className="w-4 h-4 inline mr-2" />
              Back to Pricing
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;
